import 'leaflet/dist/leaflet.css'
import * as L from 'leaflet'
import { useRef, useState, useEffect } from 'react'
import { useMap, MapContainer, TileLayer, GeoJSON } from 'react-leaflet'
import { useEmitters } from '../../contexts/emitters.context'
import MapEntity from './map-entity'
import SideBar from './Sidebar'
import styles from './styles.module.scss'
import { GeoJsonObject } from 'geojson'
import MapControl from './MapControl'
import ScaleCircle from './ScaleCircle'
import DistanceScale from './DistanceScale'
import TileLayerSelector, { TILESETS, TilesetKey } from './TileLayerSelector'

L.TileLayer.prototype.options.referrerPolicy = 'strict-origin-when-cross-origin'

const MapRefCapture: React.FC<{ mapRef: React.MutableRefObject<L.Map | null> }> = ({ mapRef }) => {
  const map = useMap()
  useEffect(() => {
    mapRef.current = map
  }, [map])
  return null
}

delete (L.Icon.Default.prototype as any)._getIconUrl
;(L.Icon.Default.prototype as any)._getIconUrl = function (name: string): string {
  return require('leaflet/dist/images/' + (name === 'icon' ? 'marker-icon' : 'marker-shadow') + '.png')
}

const Map: React.FC = () => {
  const [uploadedKmls, setUploadedKmls] = useState<GeoJsonObject[]>([])
  const [isPopupHovered, setIsPopupHovered] = useState(false)
  const mapRef = useRef<L.Map | null>(null)

  const {
    frequencyAssignmentsByCoord,
    displayingFrequencyAssignmentIds,
    areUploadsVisible, // this refers to uploaded KMLs, not frequency assignments.
  } = useEmitters()

  /*
   * The 0-based position in `frequencyAssignmentsByCoord` of what is currently selected.
   * This refers to the *pin*, not one of the frequency assignments at the pin.
   */
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [showScaleCircle, setShowScaleCircle] = useState(false)
  const [activeTileset, setActiveTileset] = useState<TilesetKey>('topo')
  const [emitterDrawerOpen, setEmitterDrawerOpen] = useState(true)

  useEffect(() => {
    // Reset to open whenever a new pin is selected
    setEmitterDrawerOpen(true)
  }, [selectedIndex])

  return (
    <div className={styles.mapWrapper}>
      <SideBar
        mapRef={mapRef}
        showScaleCircle={showScaleCircle}
        onToggleScaleCircle={() => setShowScaleCircle((v) => !v)}
        onKmlUpload={(dataSource) => {
          setUploadedKmls((prev) => [...prev, dataSource])
        }}
      />
      <MapContainer
        id="map"
        // Turn off interaction with the map when using the popup
        zoom={4}
        minZoom={3}
        zoomControl={false}
        dragging={!isPopupHovered}
        scrollWheelZoom={!isPopupHovered}
        doubleClickZoom={!isPopupHovered}
        className={styles.leafletContainer}
        attributionControl={false}
        center={[38.365525, -95.931632]}
        worldCopyJump={false}
        maxBoundsViscosity={1.0}
        maxBounds={[
          [-90, -180],
          [90, 180],
        ]}
      >
        <MapRefCapture mapRef={mapRef} />
        <MapControl isPopupHovered={isPopupHovered} />
        {showScaleCircle && <ScaleCircle />}
        <DistanceScale drawerExpanded={selectedIndex !== null && emitterDrawerOpen} />
        <TileLayerSelector selected={activeTileset} onChange={setActiveTileset} />
        <TileLayer
          key={activeTileset}
          noWrap
          url={TILESETS.find((t) => t.key === activeTileset)!.url}
          subdomains={TILESETS.find((t) => t.key === activeTileset)?.subdomains ?? ''}
        />
        {/* We draw uploads first so they have a lower z-index than markers: */}
        {areUploadsVisible && uploadedKmls.map((data, index) => <GeoJSON key={index} data={data} />)}
        {frequencyAssignmentsByCoord.map((points, index) => {
          const point = points[0]

          if (!point) return null

          // If the user is filtering by id_override, only show pins that match.
          // We will still show *all* ids at that pin.
          if (
            displayingFrequencyAssignmentIds.size &&
            !points.some((p) => displayingFrequencyAssignmentIds.has(p.id_override))
          )
            return null

          return (
            <MapEntity
              isPopupHovered={isPopupHovered}
              setIsPopupHovered={setIsPopupHovered}
              key={point.request_frequency_group_id}
              points={points}
              groupIndex={index}
              isSelected={index === selectedIndex}
              onSelect={(p) => {
                setSelectedIndex((_prev) => p.index)
              }}
              onDrawerOpenChange={setEmitterDrawerOpen}
            />
          )
        })}
      </MapContainer>
    </div>
  )
}

export default Map
